/**
 * =================================================================
 * SCRIPT-NAME:   SpeedTest.js
 * BESCHREIBUNG:   Stellt die Seite für den Geschwindigkeitstest der
 * Internetverbindung des Nutzers dar.
 *
 * VERSION:       v1.i4
 * DATUM:         2025-07-19 16:51:40
 *
 * ÄNDERUNGEN ZUR VORVERSION (v1.i3):
 * - BackButton-Komponente um 'to="/admin"' Prop erweitert, um
 * eine explizite Rückkehr zum Dashboard sicherzustellen.
 * =================================================================
 */

import React, { useState, useEffect } from 'react';
import BackButton from "./BackButton";
import "./SpeedTest.css"; 

export default function SpeedTest() {
    const [ping, setPing] = useState('--');
    const [downloadSpeed, setDownloadSpeed] = useState('--');
    const [uploadSpeed, setUploadSpeed] = useState('--');
    const [connectionType, setConnectionType] = useState('Unbekannt');
    const [isTesting, setIsTesting] = useState(false);
    const [testStep, setTestStep] = useState('Bereit'); 

    useEffect(() => {
        if (navigator.connection && navigator.connection.type) {
            setConnectionType(navigator.connection.type);
        }
    }, []);

    const testPing = async () => {
        setTestStep('Teste Ping...');
        const startTime = performance.now();
        try {
            await fetch('/favicon.ico?_=' + new Date().getTime(), { method: 'HEAD', cache: 'no-store' });
            const endTime = performance.now();
            const latency = Math.round(endTime - startTime);
            setPing(latency);
            return latency;
        } catch (error) {
            console.error("Ping Test Fehler:", error);
            setPing('Fehler');
            return null;
        }
    };

    const testDownload = async () => {
        setTestStep('Teste Download...');
        setDownloadSpeed(0); 
        const startTime = performance.now();
        let loadedBytes = 0;
        
        try {
            const response = await fetch('https://images.unsplash.com/photo-1524169358666-79f22534bc6e?_=' + new Date().getTime());
            const reader = response.body.getReader();
            
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                
                loadedBytes += value.length;
                const durationInSeconds = (performance.now() - startTime) / 1000;
                const speedMbps = (loadedBytes * 8) / (durationInSeconds * 1000 * 1000);
                setDownloadSpeed(speedMbps.toFixed(2));
            }
        } catch (error) {
            console.error("Download Test Fehler:", error);
            setDownloadSpeed('Fehler');
        }
    };
    
    const testUpload = async () => {
        setTestStep('Teste Upload...');
        setUploadSpeed(0); 
        
        const uploadEndpoint = '/api/speedtest_upload.php'; 
        const dataSizeMB = 5; 
        const data = new Blob([new ArrayBuffer(dataSizeMB * 1024 * 1024)], { type: 'application/octet-stream' });

        try {
            const startTime = performance.now();
            await fetch(uploadEndpoint, {
                method: 'POST',
                body: data,
            });
            const durationInSeconds = (performance.now() - startTime) / 1000;
            const speedMbps = (data.size * 8) / (durationInSeconds * 1000 * 1000);
            setUploadSpeed(speedMbps.toFixed(2));
        } catch (error) {
            console.error("Upload Test Fehler:", error);
            setUploadSpeed('Fehler'); 
            alert(`Hinweis: Der Upload-Test erfordert ein spezielles Backend-Skript unter ${uploadEndpoint}, das aktuell nicht vorhanden ist.`);
        }
    };

    const handleStartTest = async () => {
        setIsTesting(true);
        setTestStep('Starte...');
        setPing('--');
        setDownloadSpeed('--');
        setUploadSpeed('--');
        
        if (navigator.connection && navigator.connection.type) {
            setConnectionType(navigator.connection.type);
        }

        await testPing();
        await new Promise(resolve => setTimeout(resolve, 500));
        await testDownload();
        await new Promise(resolve => setTimeout(resolve, 500));
        await testUpload();
        
        setTestStep('Fertig');
        setIsTesting(false);
    };

    return (
        <div className="speedtest-page-wrapper">
            <div className="speedtest-content-box">
                <header className="speedtest-header">
                    <h2 className="speedtest-title">🚀 Geschwindigkeitstest</h2>
                </header>
                
                <div className="speedtest-body">
                    <p>
                        Teste die Geschwindigkeit deiner aktuellen Internetverbindung.
                    </p>
                    
                    <table className="speedtest-results-table">
                        <thead>
                            <tr>
                                <th>Ping (Latenz)</th>
                                <th>Download</th>
                                <th>Upload</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <span className="result-value">{ping}</span>
                                    <span className="result-unit">ms</span>
                                </td>
                                <td>
                                    <span className="result-value">{downloadSpeed}</span>
                                    <span className="result-unit">Mbps</span>
                                </td>
                                <td>
                                    <span className="result-value">{uploadSpeed}</span>
                                    <span className="result-unit">Mbps</span>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="speedtest-status">
                        Status: {testStep} | Verbindungstyp: {connectionType}
                    </div>

                    <button className="start-test-button" onClick={handleStartTest} disabled={isTesting}>
                        {isTesting ? 'Teste...' : 'Test starten'}
                    </button>
                </div>

                <div className="speedtest-footer">
                    {/* --- HIER IST DIE ÄNDERUNG --- */}
                    <BackButton to="/admin" />
                </div>
            </div>
        </div>
    );
}
